import { ReactNode, createContext, useContext, useState, useEffect } from 'react'
import { Rates } from 'services/pay/models'
import Cookies from 'js-cookie'
import { persistDisplaySettingsCookie } from 'utils/displaySettingsCookie'
import { isJsonString } from 'utils/isValidJson'
import { parseJson } from 'utils/json'

export type Markets =
  | 'tr'
  | 'ae'
  | 'sa'
  | 'eg'
  | 'us'
  | 'gb'
  | 'ru'
  | 'de'
  | 'es'
  | 'kw'
  | 'bh'
  | 'jo'
  | 'qa'
  | 'pk'
  | 'in'
  | 'om'
  | 'kz'
  | 'uz'
  | 'tj'
  | 'kg'
  | 'lb'
  | 'ca'
  | 'au'
  | 'nz'
  | 'za'
  | 'at'
  | 'ch'
  | 'iq'
  | 'ma'
  | 'ge'
  | 'az'
  | 'mx'
  | 'pl'
  | 'it'
  | 'fr'
export type FullLocale = string

type Currency = { iso: string; conversion: number }
interface AppContextstate {
  market: Markets
  domain: string
  endpoint?: string
  fullLocale: FullLocale
  headerLinks?: any
  currencyConversions?: Rates
  path_locale?: string
  language: string
  host?: string
  hostname?: string
  currency?: { iso: string; conversion: number }
  expansionLocale?: string
  searchFormVariant?: string
  directFlightLabelVariant?: 'A' | 'B'
}

interface AppContextActions {
  setCurrency: (currency: Currency) => void
}

export const AppContext = createContext<Partial<AppContextstate & AppContextActions>>({})

export const useAppState = () => {
  return useContext(AppContext)
}

type AppStateContextProps = {
  children: ReactNode
  value: AppContextstate
}

export const AppStateProvider = (props: AppStateContextProps) => {
  const [currency, setCurrency] = useState<Currency>(props.value?.currency ?? { iso: 'TRY', conversion: 1 })

  function changeCurrency(c: Currency) {
    // Rates values are strings, so we need to parse them to numbers
    const conversionValue = props?.value?.currencyConversions?.[c?.iso as keyof Rates]
    const conversion = conversionValue ? parseFloat(conversionValue) : 1
    setCurrency({
      iso: c?.iso,
      conversion,
    })
  }

  // Check displaySettings cookie on client load and sync currency if different
  useEffect(() => {
    if (typeof window === 'undefined') return

    const displaySettingsCookie = Cookies.get('displaySettings')
    if (!displaySettingsCookie) return

    try {
      const decodedDisplaySettings = decodeURIComponent(displaySettingsCookie)
      if (!isJsonString(decodedDisplaySettings)) return

      const parsedDisplaySettings = parseJson(decodedDisplaySettings)
      const currencyFromDisplaySettings = parsedDisplaySettings?.currency

      if (currencyFromDisplaySettings?.iso) {
        // Update conversion dynamically from currencyConversions
        // Rates values are strings, so we need to parse them to numbers
        const conversionValue = props.value?.currencyConversions?.[currencyFromDisplaySettings.iso as keyof Rates]
        const updatedConversion = conversionValue ? parseFloat(conversionValue) : currencyFromDisplaySettings.conversion ?? 1

        // If currency is different from current state, update it
        if (currency.iso !== currencyFromDisplaySettings.iso || currency.conversion !== updatedConversion) {
          setCurrency({
            iso: currencyFromDisplaySettings.iso,
            conversion: updatedConversion,
          })

          // Also update displaySettings cookie with correct conversion
          const updatedDisplaySettings = {
            ...parsedDisplaySettings,
            currency: {
              iso: currencyFromDisplaySettings.iso,
              conversion: updatedConversion,
            },
          }
          persistDisplaySettingsCookie(updatedDisplaySettings)
        }
      }
    } catch (error) {
      console.error('[AppContext] Error parsing displaySettings cookie:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run only once on mount to sync with displaySettings cookie

  const $values: AppContextstate & AppContextActions = {
    ...props.value,
    currency,
    setCurrency: changeCurrency,
  }

  return <AppContext.Provider {...props} value={$values} />
}
